import {
  adminUpdateUserSettings,
  listUsers,
  type UserProfile,
} from '../../services/appsync-client'
import { loadMyProfile } from '../../services/profile-service'
import { resolveProviderLevel } from '../../utils/rbac'
import {
  DIAL_TONE_ORG_CONFIG_KEY,
  emptyDialToneOrgConfig,
  normalizeDialToneOrgConfig,
  withSeededSoundCatalogs,
  type DialToneOrgConfig,
} from '@shared/dial-tones/types'

function parseSettingsRoot(raw: unknown): Record<string, unknown> {
  if (!raw) return {}
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as Record<string, unknown>
    } catch {
      return {}
    }
  }
  return raw as Record<string, unknown>
}

export function getDialToneOrgConfig(
  user: { settingsJson?: unknown } | null | undefined,
): DialToneOrgConfig | null {
  if (!user) return null
  const root = parseSettingsRoot(user.settingsJson)
  return normalizeDialToneOrgConfig(root[DIAL_TONE_ORG_CONFIG_KEY])
}

/** First L4 Provider Owner with a dial-tone config wins (same pattern as on-call).
 *  If no L4 row is present (Independents / Members get a filtered roster with the
 *  org catalog attached to whichever peer we could reach), any row that carries
 *  `dialToneOrgConfig` is accepted. */
export function resolveOrgDialToneConfig(
  users: Array<{ settingsJson?: unknown; roleType?: string | null; providerLevel?: number | null }>,
): DialToneOrgConfig | null {
  let fallback: DialToneOrgConfig | null = null
  for (const user of users) {
    const config = getDialToneOrgConfig(user)
    if (!config) continue
    if (resolveProviderLevel(user) >= 4) return config
    fallback ??= config
  }
  return fallback
}

let cachedConfig: DialToneOrgConfig | null | undefined
let cacheLoadedAt = 0
const CACHE_TTL_MS = 60_000

/** Warm / read cached org dial-tone config (callers use this on outbound ring). */
export function getCachedOrgDialToneConfig(): DialToneOrgConfig | null {
  if (cachedConfig === undefined) return null
  if (Date.now() - cacheLoadedAt > CACHE_TTL_MS) return cachedConfig
  return cachedConfig
}

/**
 * Cache inspection for dial-plan loaders: `loaded` is false until the first
 * refresh completes (success or empty). Avoids treating "no org tones" as a
 * cache miss that blocks connecting-tone playback on every answer.
 *
 * `stale` (2026-08-18 "old dial tone still flashes in" fix): a config past the
 * freshness TTL is STILL returned as loaded. Org tones change rarely, so the
 * sync dial-plan path must keep serving the last-known config (no audible
 * built-in → org swap on every call placed >60s after the last refresh);
 * callers kick a background refresh when `stale` is true.
 */
export function getCachedOrgDialToneConfigState(): {
  loaded: boolean
  config: DialToneOrgConfig | null
  stale: boolean
} {
  if (cachedConfig === undefined) return { loaded: false, config: null, stale: false }
  return {
    loaded: true,
    config: cachedConfig,
    stale: Date.now() - cacheLoadedAt > CACHE_TTL_MS,
  }
}

export function setCachedOrgDialToneConfig(config: DialToneOrgConfig | null): void {
  cachedConfig = config
  cacheLoadedAt = Date.now()
}

export async function refreshOrgDialToneConfig(): Promise<DialToneOrgConfig | null> {
  try {
    const users = await listUsers(100)
    const config = resolveOrgDialToneConfig(users)
    setCachedOrgDialToneConfig(config)
    return config
  } catch {
    return getCachedOrgDialToneConfig()
  }
}

/** Load config for the settings editor (prefer the signed-in L4 user's own row). */
export async function loadDialToneOrgConfigForEditor(): Promise<DialToneOrgConfig> {
  const me = await loadMyProfile()
  const mine = getDialToneOrgConfig(me)
  if (mine) {
    const seeded = withSeededSoundCatalogs(mine)
    setCachedOrgDialToneConfig(seeded)
    return seeded
  }
  const resolved = await refreshOrgDialToneConfig()
  if (resolved) return withSeededSoundCatalogs(resolved)
  return emptyDialToneOrgConfig(me.userId)
}

export async function saveDialToneOrgConfig(
  config: DialToneOrgConfig,
): Promise<UserProfile> {
  const me = await loadMyProfile()
  const next: DialToneOrgConfig = {
    ...config,
    updatedAt: new Date().toISOString(),
    updatedBy: me.userId,
  }
  const profile = await adminUpdateUserSettings({
    userId: me.userId,
    settingsPatch: {
      [DIAL_TONE_ORG_CONFIG_KEY]: next,
    },
  })
  setCachedOrgDialToneConfig(next)
  return profile
}
